// Word lookup engine for the unscrambler family. The dictionary is the public
// domain ENABLE list (~168k words), shipped gzipped in public/wordlists and
// fetched lazily on first use: it never enters the JS bundle, and the service
// worker's static cache keeps repeat visits instant.

export type WordIndex = {
  bySignature: Map<string, string[]>;
  wordCount: number;
};

let indexPromise: Promise<WordIndex> | null = null;

function signatureOf(word: string): string {
  return [...word].sort().join('');
}

async function buildIndex(): Promise<WordIndex> {
  const response = await fetch('/wordlists/enable1.txt.gz');
  if (!response.ok || !response.body) {
    throw new Error('The word list could not be loaded. Check your connection and retry.');
  }

  const stream = response.body.pipeThrough(new DecompressionStream('gzip'));
  const text = await new Response(stream).text();

  const bySignature = new Map<string, string[]>();
  let wordCount = 0;
  for (const word of text.split('\n')) {
    if (word.length < 2) continue;
    wordCount++;
    const signature = signatureOf(word);
    const bucket = bySignature.get(signature);
    if (bucket) bucket.push(word);
    else bySignature.set(signature, [word]);
  }

  return { bySignature, wordCount };
}

export function loadWordIndex(): Promise<WordIndex> {
  if (!indexPromise) {
    indexPromise = buildIndex().catch((error) => {
      // A failed fetch must not poison the singleton forever.
      indexPromise = null;
      throw error;
    });
  }
  return indexPromise;
}

// True if `candidate`'s letters fit inside `available` (both signatures, i.e.
// sorted). Two-pointer over sorted strings: O(n).
function fitsWithin(candidate: string, available: string): boolean {
  let i = 0;
  for (let j = 0; j < available.length && i < candidate.length; j++) {
    if (candidate[i] === available[j]) i++;
  }
  return i === candidate.length;
}

export type UnscrambleOptions = {
  mustContain?: string;
  minLength?: number;
  maxLength?: number;
  limit?: number;
};

export function unscramble(
  index: WordIndex,
  letters: string,
  options: UnscrambleOptions = {},
): string[] {
  const cleaned = letters.toLowerCase().replace(/[^a-z]/g, '');
  if (cleaned.length < 2) return [];

  const available = signatureOf(cleaned);
  const minLength = Math.max(2, options.minLength ?? 2);
  const maxLength = Math.min(cleaned.length, options.maxLength ?? cleaned.length);
  const mustContain = (options.mustContain ?? '').toLowerCase().replace(/[^a-z]/g, '');
  const limit = options.limit ?? 500;

  const results: string[] = [];
  for (const [signature, words] of index.bySignature) {
    if (signature.length < minLength || signature.length > maxLength) continue;
    if (!fitsWithin(signature, available)) continue;
    for (const word of words) {
      if (mustContain && !mustContain.split('').every((ch) => word.includes(ch))) continue;
      results.push(word);
    }
  }

  results.sort((a, b) => b.length - a.length || a.localeCompare(b));
  return results.slice(0, limit);
}

export function solveAnagrams(index: WordIndex, word: string): string[] {
  const cleaned = word.toLowerCase().replace(/[^a-z]/g, '');
  if (cleaned.length < 2) return [];
  const exact = index.bySignature.get(signatureOf(cleaned)) ?? [];
  return exact.filter((candidate) => candidate !== cleaned);
}

export function groupByLength(words: string[]): Array<{ length: number; words: string[] }> {
  const groups = new Map<number, string[]>();
  for (const word of words) {
    const bucket = groups.get(word.length);
    if (bucket) bucket.push(word);
    else groups.set(word.length, [word]);
  }
  return [...groups.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([length, list]) => ({ length, words: list }));
}
